import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { colors } from '../constants/colors';

const LoadingScreen = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Simulate loading progress
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(() => setIsLoading(false), 500);
          return 100;
        }
        return prev + 2;
      });
    }, 30);

    return () => clearInterval(interval);
  }, []);

  const containerVariants = {
    initial: { 
      opacity: 1,
      scale: 1,
    },
    exit: { 
      opacity: 0,
      scale: 1.1,
      transition: { 
        duration: 0.8, 
        ease: [0.4, 0, 0.2, 1] 
      }
    }
  };

  const logoVariants = {
    initial: { 
      scale: 0,
      rotate: -180,
      opacity: 0
    },
    animate: { 
      scale: 1,
      rotate: 0,
      opacity: 1,
      transition: { 
        duration: 1.2, 
        ease: [0.4, 0, 0.2, 1],
        type: "spring",
        stiffness: 100
      }
    }
  };

  const textVariants = {
    initial: { 
      y: 50,
      opacity: 0
    },
    animate: { 
      y: 0,
      opacity: 1,
      transition: { 
        duration: 0.8,
        delay: 0.5,
        ease: [0.4, 0, 0.2, 1]
      }
    }
  };

  const progressBarVariants = {
    initial: { 
      width: "0%",
      opacity: 0
    },
    animate: { 
      width: `${progress}%`,
      opacity: 1,
      transition: { 
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  const particleVariants = {
    initial: { 
      scale: 0,
      opacity: 0
    },
    animate: (i) => ({
      scale: [0, 1, 0],
      opacity: [0, 1, 0],
      y: [-20, -100],
      x: [0, (Math.random() - 0.5) * 100],
      transition: {
        duration: 2,
        delay: i * 0.1,
        repeat: Infinity,
        repeatDelay: Math.random() * 2
      }
    })
  };

  const particles = Array.from({ length: 20 }, (_, i) => i);

  return (
    <AnimatePresence>
      {isLoading && (
        <motion.div
          className="loading-screen"
          variants={containerVariants}
          initial="initial"
          exit="exit"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            zIndex: 9999,
            overflow: 'hidden'
          }}
        >
          {/* Animated Particles */}
          {particles.map((i) => (
            <motion.div
              key={i}
              custom={i}
              variants={particleVariants}
              initial="initial"
              animate="animate"
              style={{
                position: 'absolute',
                width: 4,
                height: 4,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
              }}
            />
          ))}

          {/* Main Logo/Name */}
          <motion.div
            variants={logoVariants}
            initial="initial"
            animate="animate"
            style={{
              textAlign: 'center',
              marginBottom: '2rem'
            }}
          >
            <motion.h1
              style={{
                fontSize: '4rem',
                fontWeight: 'bold',
                color: 'white',
                margin: 0,
                textShadow: '0 4px 20px rgba(0,0,0,0.3)',
                fontFamily: 'Poppins, sans-serif'
              }}
            >
              MAKITE
            </motion.h1>
            <motion.p
              variants={textVariants}
              initial="initial"
              animate="animate"
              style={{
                fontSize: '1.5rem',
                color: 'rgba(255, 255, 255, 0.9)',
                margin: '0.5rem 0 0 0',
                fontFamily: 'Roboto Mono, monospace'
              }}
            >
              Software Developer
            </motion.p>
          </motion.div>

          {/* Loading Progress Bar */}
          <motion.div
            variants={textVariants}
            initial="initial"
            animate="animate"
            style={{
              width: '300px',
              textAlign: 'center'
            }}
          >
            <div
              style={{
                width: '100%',
                height: '4px',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '2px',
                overflow: 'hidden',
                marginBottom: '1rem'
              }}
            >
              <motion.div
                variants={progressBarVariants}
                initial="initial"
                animate="animate"
                key={progress}
                style={{
                  height: '100%',
                  backgroundColor: 'white',
                  borderRadius: '2px',
                  boxShadow: '0 0 10px rgba(255, 255, 255, 0.5)'
                }}
              />
            </div>
            <motion.p
              style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.9rem',
                fontFamily: 'Roboto Mono, monospace',
                margin: 0
              }}
            >
              Loading amazing portfolio... {progress}%
            </motion.p>
          </motion.div>

          {/* Animated Elements */}
          <motion.div
            style={{
              position: 'absolute',
              bottom: '50px',
              display: 'flex',
              gap: '10px'
            }}
          >
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                animate={{
                  scale: [1, 1.5, 1],
                  opacity: [0.3, 1, 0.3],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  delay: i * 0.2,
                }}
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: 'white'
                }}
              />
            ))}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingScreen;
